import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import classNames from 'classnames';
import { HotKeys } from 'react-hotkeys';

import { Dropdown } from '@eventbrite/legacy-eds.containers';
import { NavList } from '@eventbrite/legacy-eds.nav-list';
import { TextList } from '@eventbrite/legacy-eds.text-list';
import { DropLink } from './DropLink';
import { ALIGN_LEFT } from '@eventbrite/legacy-eds.containers/constants';
import { getAdditionalProps } from '@eventbrite/legacy-eds.utils/props';
import { isMediumDown } from '@eventbrite/legacy-eds.utils/media-queries';
import { ITEMS_PROP_TYPE } from '@eventbrite/legacy-eds.nav-list/constants';
import { TEXT_ITEMS_PROP_TYPE } from '@eventbrite/legacy-eds.text-list';

import {
    TABBABLE_INDEX,
    UNTABBABLE_INDEX,
    HIDE_CHILD,
    SHOW_CHILD,
} from '@eventbrite/legacy-eds.hot-keys';
import { ACTION_KEY_MAP } from './hotKeys';
import styles from './DropdownMenu.module.scss';

const FOCUSED_POSITION = 0;
const UNFOCUSED_POSITION = -1;

export const DropdownMenuList = ({
    show,
    navItems,
    textItems,
    align,
    dropUp,
    onHide,
    onTextItemSelect,
    defaultFocusPosition,
}) => {
    let menu = null;
    let list;

    if (textItems) {
        list = (
            <TextList
                items={textItems}
                onItemSelect={onTextItemSelect}
                defaultFocusPosition={defaultFocusPosition}
            />
        );
    } else if (navItems) {
        list = (
            <NavList
                items={navItems}
                defaultFocusPosition={defaultFocusPosition}
                hideDropdown={onHide}
            />
        );
    }

    if (show) {
        menu = (
            <Dropdown align={align} dropUp={dropUp} onClickOutside={onHide}>
                {list}
            </Dropdown>
        );
    }

    return menu;
};

export class DropdownMenu extends React.PureComponent {
    static propTypes = {
        children: PropTypes.node.isRequired,
        fallbackUrl: PropTypes.string,
        textItems: TEXT_ITEMS_PROP_TYPE,
        navItems: ITEMS_PROP_TYPE,
        dropdownAlign: Dropdown.propTypes.align,
        dropUp: Dropdown.propTypes.dropUp,
        preventMouseOver: PropTypes.bool,
        onTextItemSelect: PropTypes.func,
        hideDropIconWhenNarrow: PropTypes.bool,
        onMouseEnterDropdownMenu: PropTypes.func,
        onClickDropdown: PropTypes.func,
        label: PropTypes.string,
        role: PropTypes.string,
    };

    static defaultProps = {
        dropdownAlign: ALIGN_LEFT,
        dropUp: false,
        preventMouseOver: false,
        hideDropIconWhenNarrow: false,
    };

    state = {
        showDropdown: false,
        tabIndex: TABBABLE_INDEX,
        focusPosition: UNFOCUSED_POSITION,
        shouldFocusDropLink: false,
    };

    hotKeyHandlers = {
        [HIDE_CHILD]: this._hideDropdown.bind(this, { shouldFocusDropLink: true }),
        [SHOW_CHILD]: (e) => this._showDropdown({ shouldFocusChild: true, isKeyEvent: true }, e),
    };

    _hideDropdown({ shouldFocusDropLink = false } = {}) {
        this.setState({
            showDropdown: false,
            tabIndex: TABBABLE_INDEX,
            focusPosition: UNFOCUSED_POSITION,
            shouldFocusDropLink,
        });
    }

    _showDropdown(
        { shouldFocusChild = false, isKeyEvent = false, shouldFocusDropLink = false } = {},
        e = false,
    ) {
        if (isKeyEvent) {
            e.preventDefault();
            e.stopPropagation();
        }

        this.setState({
            showDropdown: true,
            tabIndex: isKeyEvent ? UNTABBABLE_INDEX : TABBABLE_INDEX,
            focusPosition: shouldFocusChild ? FOCUSED_POSITION : UNFOCUSED_POSITION,
            shouldFocusDropLink,
        });
    }

    _handleLinkClick(e) {
        e.preventDefault();
        e.stopPropagation();

        const { showDropdown } = this.state;
        const { onClickDropdown } = this.props;
        if (onClickDropdown) {
            onClickDropdown({ showingDropdown: !showDropdown });
        }
        if (showDropdown) {
            this._hideDropdown();
        } else {
            this._showDropdown();
        }
    }

    _handleMouseOver() {
        this._showDropdown({ shouldFocusDropLink: true });
    }

    _handleMouseLeave() {
        this._hideDropdown({ shouldFocusDropLink: false });
    }

    _handleTextItemSelect(value, index) {
        const { onTextItemSelect } = this.props;
        this._hideDropdown();
        if (onTextItemSelect) {
            onTextItemSelect(value, index);
        }
    }

    render() {
        const {
            children,
            fallbackUrl,
            navItems,
            textItems,
            dropdownAlign,
            dropUp,
            preventMouseOver,
            hideDropIconWhenNarrow,
            onMouseEnterDropdownMenu,
            label,
            role,
        } = this.props;
        const { showDropdown, tabIndex, focusPosition, shouldFocusDropLink } = this.state;
        const additionalProps = getAdditionalProps(this);
        const { className = '' } = additionalProps;
        const classes = classNames(styles.root, { [styles.active]: showDropdown }, className);

        let _handleMouseOver;
        let _handleMouseLeave;

        if (!isMediumDown() && !preventMouseOver) {
            _handleMouseOver = this._handleMouseOver.bind(this);
            _handleMouseLeave = this._handleMouseLeave.bind(this);
        }

        return (
            <HotKeys keyMap={ACTION_KEY_MAP} handlers={this.hotKeyHandlers}>
                <div
                    data-testid="dropdown-menu"
                    role={role ?? 'menubar'}
                    tabIndex="-1"
                    {...additionalProps}
                    className={classes}
                    onMouseOver={_handleMouseOver}
                    onMouseLeave={_handleMouseLeave}
                    onFocus={noop}
                    onMouseEnter={onMouseEnterDropdownMenu}
                >
                    <DropLink
                        fallbackUrl={fallbackUrl}
                        dropUp={dropUp}
                        onClick={this._handleLinkClick.bind(this)}
                        tabIndex={tabIndex}
                        preventMouseOver={preventMouseOver}
                        showDropdown={showDropdown}
                        hideDropIconWhenNarrow={hideDropIconWhenNarrow}
                        shouldFocusDropLink={shouldFocusDropLink}
                        ariaLabelContent={label}
                        role={role}
                    >
                        {children}
                    </DropLink>
                    <DropdownMenuList
                        show={showDropdown}
                        navItems={navItems}
                        textItems={textItems}
                        align={dropdownAlign}
                        dropUp={dropUp}
                        defaultFocusPosition={focusPosition}
                        onHide={this._hideDropdown.bind(this)}
                        onTextItemSelect={this._handleTextItemSelect.bind(this)}
                    />
                </div>
            </HotKeys>
        );
    }
}
